import { Broadcast } from '../types/broadcast.types';

export const getStatusColor = (status: string, isScheduled?: boolean): string => {
    if (status === 'pending' && isScheduled) {
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
    }
    switch (status) {
        case 'pending':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'processing':
            return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'sent':
            return 'bg-green-100 text-green-800 border-green-200';
        case 'partial':
            return 'bg-amber-100 text-amber-900 border-amber-200';
        case 'failed':
            return 'bg-red-100 text-red-800 border-red-200';
        case 'cancelled':
            return 'bg-gray-100 text-gray-800 border-gray-200';
        case 'translating':
            return 'bg-purple-100 text-purple-800 border-purple-200';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};

export const getStatusLabel = (status: string, isScheduled?: boolean): string => {
    if (status === 'pending' && isScheduled) {
        return 'Agendado';
    }
    switch (status) {
        case 'pending':
            return 'Pendente';
        case 'processing':
            return 'Enviando';
        case 'sent':
            return 'Enviado';
        case 'partial':
            return 'Enviado parcialmente';
        case 'failed':
            return 'Falhou';
        case 'cancelled':
            return 'Cancelado';
        case 'translating':
            return 'Traduzindo';
        default:
            return status;
    }
};

export const sortBroadcasts = (
    broadcasts: Broadcast[],
    sortBy: 'sent_count' | 'success_rate' | 'created_at',
    sortOrder: 'asc' | 'desc'
): Broadcast[] => {
    return [...broadcasts].sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
            case 'sent_count':
                comparison = (a.sent_count || 0) - (b.sent_count || 0);
                break;
            case 'success_rate':
                comparison = (a.success_rate || 0) - (b.success_rate || 0);
                break;
            case 'created_at':
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                if (comparison === 0) comparison = a.id - b.id;
                break;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });
};

export const filterBroadcasts = (
    broadcasts: Broadcast[],
    page?: string,
    status?: string,
    targetLanguage?: string
): Broadcast[] => {
    return broadcasts.filter(broadcast => {
        if (page && !broadcast.pages.some(p => p.page_id === page)) {
            return false;
        }
        if (status && broadcast.status !== status) {
            return false;
        }
        if (targetLanguage) {
            if (targetLanguage === '__none__') {
                // Only broadcasts without AI translation
                if (broadcast.ai_translate) return false;
            } else {
                // Broadcasts with a specific target language
                if (!broadcast.ai_translate || broadcast.target_language !== targetLanguage) return false;
            }
        }
        return true;
    });
};

export const formatSuccessRate = (rate?: number): string => {
    if (rate === undefined || rate === null) return '--';
    return `${rate.toFixed(1)}%`;
};

/**
 * Formata uma duração em segundos para um texto legível.
 * Ex.: 45 -> "45s", 623 -> "10min 23s", 3720 -> "1h 02min".
 * Retorna '–' para valores nulos/inválidos.
 */
export const formatDuration = (seconds?: number | null): string => {
    if (seconds === undefined || seconds === null || isNaN(seconds) || seconds < 0) return '–';
    const total = Math.round(seconds);
    if (total < 60) return `${total}s`;

    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (mins < 60) {
        return secs > 0 ? `${mins}min ${secs}s` : `${mins}min`;
    }

    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hours}h ${String(remMins).padStart(2, '0')}min`;
};

/**
 * Formata um instante ISO 8601 UTC em um fuso horário arbitrário, em pt-BR (data + hora).
 * - null/undefined → "—"
 * - timeZone inválido → try/catch com fallback para "UTC".
 *
 * @param isoUtc   Instante ISO 8601 em UTC (ex.: "2026-06-18T09:40:00Z"). Aceita qualquer string Date-parseável.
 * @param timeZone Fuso IANA (ex.: "America/Sao_Paulo", "Europe/Madrid").
 * @param opts     Override das opções de Intl.DateTimeFormat (default: dia/mês + hora:min).
 */
export const formatInTz = (
    isoUtc: string | null | undefined,
    timeZone: string,
    opts?: Intl.DateTimeFormatOptions
): string => {
    if (isoUtc === null || isoUtc === undefined) return '—';
    const date = new Date(isoUtc);
    if (isNaN(date.getTime())) return '—';

    const baseOpts: Intl.DateTimeFormatOptions = opts ?? {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    };

    try {
        return new Intl.DateTimeFormat('pt-BR', { ...baseOpts, timeZone }).format(date);
    } catch {
        return new Intl.DateTimeFormat('pt-BR', { ...baseOpts, timeZone: 'UTC' }).format(date);
    }
};
